import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { CmdIcon } from "../shared/CmdIcon";
import { PlanningCalendar } from "./PlanningCalendar";
import { buildCalendarEvents } from "../../helpers/planning/calendarModel";
import { clusterItems, markerLeft, rowItems } from "../../helpers/planning/timelineLayout";
import { TaskSpanBar } from "./TaskSpanBar";
import {
  PLAN_KIND,
  collectUnscheduled,
  describeNode,
  dayIndexToISO,
  filterPlanningTree,
  flattenPlanning,
  focusTargetDay,
  planningBucket,
  pruneByPredicate,
  weekdayOf,
} from "../../helpers/planning/planningModel";
import { formatTargetCompletion } from "../../helpers/projectStructure";
import { nonWorkingDay, SCHEDULE_UNAVAILABLE_NOTE } from "../../helpers/planning/timelineLayout";
import "./planningDesktop.css";

const LABEL_W = 270;
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const UNSCHED_PREVIEW = 9;
const KIND_LABEL = { project: "Project", location: "Location", feature: "Feature", task: "Task" };

function initials(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return `${parts[0][0]}${parts.length > 1 ? parts[parts.length - 1][0] : ""}`.toUpperCase();
}

function hue(name) {
  let h = 0;
  for (const ch of String(name || "")) h = (h * 31 + ch.charCodeAt(0)) % 360;
  return h;
}

const utc = (index, options) =>
  new Date(index * 86400000).toLocaleDateString("en-US", { ...options, timeZone: "UTC" });

const STATUS_LABEL = { overdue: "Overdue", today: "Due today", upcoming: "Upcoming", done: "Completed", target: "Target date" };
const FOCUS_LABEL = { overdue: "Overdue", week: "This Week", upcoming: "Upcoming", none: "No Target Date" };

function useWidth(ref, deps) {
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => setWidth(el.clientWidth);
    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return width;
}

/**
 * Desktop Planning workspace (concept: KPI row, toolbar, project/task timeline
 * and an Unscheduled panel). A new presentation of the SAME Planning data the
 * mobile screen reads; dates are single due/target dates, so work is drawn as
 * chips that start on their date (no durations are implied).
 */
export function PlanningDesktop({
  today,
  data,
  loading,
  error,
  tree,
  visibleTree,
  jobOptions,
  activeFilter,
  setProjectFilter,
  locationOptions = [],
  activeLocation = "",
  setLocationFilter = () => {},
  featureOptions = [],
  activeFeature = "",
  setFeatureFilter = () => {},
  workSchedule = null,
  needsSchedule = false,
  summary,
  range,
  expanded,
  toggle,
  onOpenItem,
  view: viewProp,
  onViewChange,
  calendarSelection = null,
  onCalendarSelectionChange,
}) {
  const [viewState, setViewState] = useState("calendar");
  const view = viewProp ?? viewState;
  const setView = onViewChange ?? setViewState;
  const [search, setSearch] = useState("");
  const [showAllUnsched, setShowAllUnsched] = useState(false);
  const [focus, setFocus] = useState(null); // KPI focus: overdue | week | upcoming | none
  const [pop, setPop] = useState(null); // { cluster, rowName, x, y }
  const [scrollLeft, setScrollLeft] = useState(0);
  const scrollerRef = useRef(null);
  const didScroll = useRef(false);

  const panelWidth = useWidth(scrollerRef, [loading, data, view]);
  const trackView = Math.max(0, panelWidth - LABEL_W);
  const dayW = Math.max(76, Math.min(132, trackView ? Math.floor(trackView / 8) : 96));

  const searched = useMemo(() => filterPlanningTree(visibleTree, search), [visibleTree, search]);
  const inFocus = useCallback((node) => !focus || planningBucket(node, today) === focus, [focus, today]);
  const filtered = useMemo(
    () => (focus ? pruneByPredicate(searched, inFocus) : searched),
    [searched, focus, inFocus],
  );
  const rows = useMemo(() => flattenPlanning(filtered, expanded), [filtered, expanded]);
  const unscheduled = useMemo(() => collectUnscheduled(filtered), [filtered]);
  const calendarEvents = useMemo(() => buildCalendarEvents(searched), [searched]);

  const trackWidth = range.days * dayW;
  const todayLeft = markerLeft(range.todayIdx, range.start, dayW);

  const scrollToToday = useCallback(
    (smooth) => {
      const el = scrollerRef.current;
      if (!el) return;
      const left = Math.max(0, (range.todayIdx - range.start - 1) * dayW);
      if (smooth && typeof el.scrollTo === "function") el.scrollTo({ left, behavior: "smooth" });
      else el.scrollLeft = left;
    },
    [range.start, range.todayIdx, dayW],
  );

  useLayoutEffect(() => {
    if (view !== "timeline") {
      didScroll.current = false;
      return;
    }
    if (loading || !data || !panelWidth || didScroll.current) return;
    didScroll.current = true;
    scrollToToday(false);
  }, [loading, data, panelWidth, view, scrollToToday]);

  useEffect(() => {
    // Keep the window anchored when the day width changes (resize).
    if (view === "timeline" && didScroll.current) scrollToToday(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayW]);

  useEffect(() => {
    if (!pop) return undefined;
    const close = (e) => {
      if (e.type === "keydown" ? e.key === "Escape" : !e.target.closest?.(".pd-pop, .pd-mark")) setPop(null);
    };
    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("pointerdown", close);
      document.removeEventListener("keydown", close);
    };
  }, [pop]);

  function openCluster(event, cluster, rowName) {
    const rect = event.currentTarget.getBoundingClientRect();
    setPop((prev) =>
      prev && prev.cluster.key === cluster.key && prev.rowName === rowName
        ? null
        : { cluster, rowName, x: Math.min(Math.max(rect.left + rect.width / 2, 190), window.innerWidth - 190), y: rect.bottom + 8, up: rect.bottom + 260 > window.innerHeight, top: rect.top - 8 },
    );
  }

  /** Put a date at (or one day before) the left edge of the timeline. */
  function scrollToDay(idx, { lead = 1, smooth = true } = {}) {
    const el = scrollerRef.current;
    if (!el || idx == null) return;
    const left = Math.max(0, (idx - range.start - lead) * dayW);
    if (smooth && typeof el.scrollTo === "function") el.scrollTo({ left, behavior: "smooth" });
    else el.scrollLeft = left;
  }

  // A KPI click sets the focus AND the initial window; afterwards the user
  // navigates freely (nothing re-applies the target on later renders).
  function chooseFocus(next) {
    const clearing = focus === next;
    setPop(null);
    if (clearing) {
      setFocus(null);
      if (view === "timeline") scrollToToday(true);
      return;
    }
    setFocus(next);
    if (view !== "timeline") return;
    const target = focusTargetDay(searched, next, today);
    if (target != null) scrollToDay(target, { lead: next === "week" ? 0 : 1 });
  }

  function nudge(days) {
    const el = scrollerRef.current;
    if (!el) return;
    const left = el.scrollLeft + days * dayW;
    if (typeof el.scrollTo === "function") el.scrollTo({ left, behavior: "smooth" });
    else el.scrollLeft = left;
  }

  const firstVisible = range.start + Math.max(0, Math.floor(scrollLeft / dayW));
  const lastVisible = Math.min(range.end, firstVisible + Math.max(1, Math.floor(trackView / dayW)) - 1);
  const rangeText = `${utc(firstVisible, { month: "short", day: "numeric" })} – ${utc(lastVisible, { month: "short", day: "numeric", year: "numeric" })}`;

  const days = [];
  for (let i = 0; i < range.days; i += 1) {
    const idx = range.start + i;
    const wd = weekdayOf(idx);
    const iso = dayIndexToISO(idx);
    days.push(
      <div key={idx} className={`pd-day${nonWorkingDay(idx, workSchedule) ? " is-weekend" : ""}${idx === range.todayIdx ? " is-today" : ""}`} style={{ width: dayW }} data-date={iso}>
        <span className="pd-day__wd">{WEEKDAYS[wd]}</span>
        <span className="pd-day__num">{Number(iso.slice(8, 10))}</span>
        {Number(iso.slice(8, 10)) === 1 || i === 0 ? <span className="pd-day__month">{utc(idx, { month: "short" })}</span> : null}
      </div>,
    );
  }
  const weekend = [];
  for (let i = 0; i < range.days; i += 1) {
    if (nonWorkingDay(range.start + i, workSchedule)) weekend.push(<span key={i} className="pd-weekend" style={{ left: i * dayW, width: dayW }} />);
  }

  const kpis = [
    { key: "overdue", focus: "overdue", tone: "red", icon: "alert", value: summary.overdue, label: "Overdue" },
    { key: "week", focus: "week", tone: "gold", icon: "calendar", value: summary.thisWeek, label: "This Week" },
    { key: "upcoming", focus: "upcoming", tone: "blue", icon: "list", value: summary.upcoming, label: "Upcoming" },
    { key: "none", focus: "none", tone: "grey", icon: "clock", value: summary.noTarget, label: "No Target Date" },
  ];

  const unschedItems = showAllUnsched ? unscheduled.items : unscheduled.items.slice(0, UNSCHED_PREVIEW);

  return (
    <div className="pd-root" data-testid="planning-screen">
      <header className="pd-head">
        <span className="pd-head__icon"><CmdIcon name="calendar" /></span>
        <div>
          <h1 className="pd-head__title">Planning</h1>
          <p className="pd-head__sub">See what’s coming up across your projects.</p>
        </div>
      </header>

      {error ? <div className="pd-error">{error}</div> : null}
      {loading ? <div className="pd-empty">Loading planning…</div> : null}

      {!loading && data ? (
        <>
          {data.fromDevice ? (
            <div className="pd-banner" role="status">Showing what’s saved on this device. Reconnect for the latest dates.</div>
          ) : null}

          <div className="pd-kpis" aria-label="Planning summary">
            {kpis.map((k) => (
              <button
                key={k.key}
                type="button"
                className={`pd-kpi pd-kpi--${k.tone}${focus === k.focus ? " is-on" : ""}`}
                aria-pressed={focus === k.focus}
                onClick={() => chooseFocus(k.focus)}
                title={focus === k.focus ? "Clear focus" : `Focus the board on ${k.label.toLowerCase()} work`}
              >
                <span className="pd-kpi__icon"><CmdIcon name={k.icon} /></span>
                <span className="pd-kpi__text">
                  <strong>{k.value}</strong>
                  <span>{k.label}</span>
                </span>
              </button>
            ))}
          </div>

          {tree.length === 0 ? (
            <div className="pd-empty">No active projects to plan yet.</div>
          ) : (
            <>
              <div className="pd-toolbar">
                <label className="pd-select">
                  <span className="pd-sr">Job</span>
                  <select aria-label="Job" value={activeFilter} onChange={(e) => setProjectFilter(e.target.value)}>
                    <option value="">All Jobs</option>
                    {jobOptions.map((job) => (
                      <option key={job.id} value={job.id}>{job.name}</option>
                    ))}
                  </select>
                </label>
                {activeFilter && locationOptions.length ? (
                  <label className="pd-select">
                    <span className="pd-sr">Location</span>
                    <select aria-label="Location" value={activeLocation} onChange={(e) => setLocationFilter(e.target.value)}>
                      <option value="">All Locations</option>
                      {locationOptions.map((o) => (
                        <option key={o.id} value={o.id}>{o.name}</option>
                      ))}
                    </select>
                  </label>
                ) : null}
                {activeLocation && featureOptions.length ? (
                  <label className="pd-select">
                    <span className="pd-sr">Feature</span>
                    <select aria-label="Feature" value={activeFeature} onChange={(e) => setFeatureFilter(e.target.value)}>
                      <option value="">All Features</option>
                      {featureOptions.map((o) => (
                        <option key={o.id} value={o.id}>{o.name}</option>
                      ))}
                    </select>
                  </label>
                ) : null}
                <label className="pd-search">
                  <CmdIcon name="search" />
                  <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search tasks…" aria-label="Search planning" />
                </label>
                {focus ? (
                  <button type="button" className="pd-focuschip" onClick={() => chooseFocus(focus)} aria-label={`Clear ${FOCUS_LABEL[focus]} focus`}>
                    Focus: {FOCUS_LABEL[focus]} <span aria-hidden="true">×</span>
                  </button>
                ) : null}
                <span className="pd-spacer" />
                {view === "timeline" ? (
                  <div className="pd-nav">
                    <button type="button" className="pd-iconbtn" aria-label="Earlier" onClick={() => nudge(-7)}>‹</button>
                    <div className="pd-range" data-testid="planning-range">{rangeText}</div>
                    <button type="button" className="pd-iconbtn" aria-label="Later" onClick={() => nudge(7)}>›</button>
                    <button type="button" className="pd-todaybtn" onClick={() => scrollToToday(true)}>Today</button>
                  </div>
                ) : null}
                <div className="pd-views" role="group" aria-label="Planning view">
                  {[["timeline", "Timeline"], ["calendar", "Calendar"]].map(([id, label]) => (
                    <button key={id} type="button" className={view === id ? "is-on" : ""} aria-pressed={view === id} onClick={() => setView(id)}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="pd-body">
                <div className="pd-main">
                  {view === "calendar" ? (
                    <div className="pd-calendar">
                      <PlanningCalendar
                        events={calendarEvents}
                        today={today}
                        minutesPerDay={workSchedule?.minutesPerDay ?? null}
                        onOpenEvent={onOpenItem}
                        initialSelected={calendarSelection?.selected}
                        initialMonth={calendarSelection?.monthStart}
                        onSelectionChange={onCalendarSelectionChange}
                      />
                    </div>
                  ) : (
                    <>
                      {needsSchedule ? (
                        <div className="pd-banner" role="status" data-testid="planning-schedule-unavailable">{SCHEDULE_UNAVAILABLE_NOTE}</div>
                      ) : null}
                      <div className="pd-panel" ref={scrollerRef} onScroll={(e) => setScrollLeft(e.currentTarget.scrollLeft)} data-testid="planning-timeline">
                        <div className="pd-inner" style={{ width: LABEL_W + trackWidth }}>
                          <div className="pd-thead">
                            <div className="pd-corner" style={{ width: LABEL_W }}>
                              <span>Projects / Tasks</span>
                              <em>{utc(firstVisible, { month: "short", year: "numeric" })}</em>
                            </div>
                            <div className="pd-days" style={{ width: trackWidth }}>
                              {days}
                              <span className="pd-today-tag" style={{ left: todayLeft }}>TODAY</span>
                            </div>
                          </div>
                          <div className="pd-rows">
                            <div className="pd-plane" aria-hidden="true" style={{ left: LABEL_W, width: trackWidth, backgroundSize: `${dayW}px 100%` }}>
                              {weekend}
                              <span className="pd-today-line" data-testid="planning-today-line" style={{ left: todayLeft }} />
                            </div>
                            {rows.length === 0 ? <div className="pd-empty">{focus ? `Nothing to show for ${FOCUS_LABEL[focus]}.` : "No planning items match your search."}</div> : null}
                            {rows.map(({ node, depth, expandable, isOpen }) => {
                              const clusters = clusterItems(rowItems(node, isOpen, { include: inFocus }), range.todayIdx);
                              const rowH = depth ? 48 : 58;
                              const sub = node.kind === PLAN_KIND.PROJECT || node.kind === PLAN_KIND.LOCATION ? describeNode(node) : "";
                              return (
                                <div key={`${node.kind}:${node.id}`} className={`pd-row pd-row--${node.kind}`} data-kind={node.kind} data-depth={depth} style={{ height: rowH }}>
                                  <div className="pd-label" style={{ width: LABEL_W, paddingLeft: 12 + depth * 18 }}>
                                    {expandable ? (
                                      <button type="button" className="pd-chev" aria-expanded={isOpen} aria-label={`${isOpen ? "Collapse" : "Expand"} ${node.name}`} onClick={() => toggle(node)}>
                                        <CmdIcon name="arrow" className={isOpen ? "is-open" : ""} />
                                      </button>
                                    ) : (
                                      <span className="pd-chev-spacer" />
                                    )}
                                    {node.kind === PLAN_KIND.PROJECT ? (
                                      <span className="pd-avatar" style={{ "--h": hue(node.name) }}>{initials(node.name)}</span>
                                    ) : null}
                                    <span className="pd-label__text">
                                      <span className={`pd-label__name${node.completed ? " is-done" : ""}`}>{node.name}</span>
                                      {sub ? <span className="pd-label__sub">{sub.replace(/ · /, " • ")}</span> : <span className="pd-label__sub">{KIND_LABEL[node.kind]}</span>}
                                    </span>
                                  </div>
                                  <div className="pd-track" style={{ width: trackWidth }}>
                                    {node.kind === PLAN_KIND.TASK ? <TaskSpanBar node={node} startIdx={range.start} endIdx={range.end} dayW={dayW} /> : null}
                                    {clusters.map((cluster) => {
                                      const names = cluster.items.map((i) => i.node.name).join(", ");
                                      const label = `${cluster.count} ${cluster.count === 1 ? "item" : "items"} on ${formatTargetCompletion(cluster.items[0].node.date)} (${STATUS_LABEL[cluster.status]}): ${names}`;
                                      return (
                                        <button
                                          type="button"
                                          key={cluster.key}
                                          className={`pd-mark pd-mark--${cluster.status}${cluster.targetOnly ? " is-target" : ""}${cluster.count > 1 ? " is-cluster" : ""}`}
                                          style={{ left: markerLeft(cluster.dayIndex, range.start, dayW) }}
                                          title={label}
                                          aria-label={label}
                                          aria-expanded={Boolean(pop && pop.cluster.key === cluster.key && pop.rowName === node.name)}
                                          data-testid={`marker-${cluster.items[0].node.kind}`}
                                          data-date={cluster.items[0].node.date}
                                          data-count={cluster.count}
                                          onClick={(e) => openCluster(e, cluster, node.name)}
                                        >
                                          {cluster.count > 1 ? <span>{cluster.count}</span> : null}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                      <ul className="pd-legend" aria-label="Legend">
                        <li><i className="pd-mark--overdue" />Overdue</li>
                        <li><i className="pd-mark--today" />Due today</li>
                        <li><i className="pd-mark--upcoming" />Upcoming</li>
                        <li><i className="pd-mark--done" />Completed</li>
                        <li><i className="pd-mark--target is-target" />Project / location / feature target date</li>
                        <li><i className="pd-mark--upcoming is-cluster">3</i>Several items on one date</li>
                        <li><i className="tk-legend-span" />Task work: start → expected completion, working days only (green = reported progress, gold = remaining)</li>
                        <li className="pd-legend__note">One marker = one due / target date. A task’s due date is its deadline marker, not the end of its bar.</li>
                      </ul>
                    </>
                  )}
                </div>

                <aside className={`pd-unsched${focus === "none" ? " is-focus" : ""}`} aria-label="Needs Scheduling" data-testid="planning-unscheduled">
                  <header className="pd-unsched__head">
                    <h2>Needs Scheduling</h2>
                    <span className="pd-unsched__count">{unscheduled.total}</span>
                  </header>
                  {unscheduled.total === 0 ? (
                    <p className="pd-unsched__empty">{search ? "No unscheduled items match your search." : "Everything has a date."}</p>
                  ) : (
                    <ul className="pd-unsched__list">
                      {unschedItems.map(({ node, projectName }) => (
                        <li key={`${node.kind}:${node.id}`}>
                          <i className={`pd-dot pd-dot--${node.kind}`} aria-hidden="true" />
                          <span>
                            <b>{node.name}</b>
                            <em>{projectName || KIND_LABEL[node.kind]}{projectName ? ` · ${KIND_LABEL[node.kind]}` : ""}</em>
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {unscheduled.total > UNSCHED_PREVIEW ? (
                    <button type="button" className="pd-unsched__all" onClick={() => setShowAllUnsched((v) => !v)} aria-expanded={showAllUnsched}>
                      {showAllUnsched ? "Show fewer" : `View All ${unscheduled.total} Needing a Date`} <CmdIcon name="arrow" />
                    </button>
                  ) : null}
                </aside>
              </div>
            </>
          )}
        </>
      ) : null}
      {pop ? (
        <div className={`pd-pop${pop.up ? " is-up" : ""}`} role="dialog" aria-label="Items on this date" style={{ left: pop.x, top: pop.up ? pop.top : pop.y }}>
          <header>
            <strong>{formatTargetCompletion(pop.cluster.items[0].node.date)}</strong>
            <span>{pop.rowName} · {pop.cluster.count} {pop.cluster.count === 1 ? "item" : "items"}</span>
          </header>
          <ul>
            {pop.cluster.items.map((item) => (
              <li key={item.key}>
                <i className={`pd-mark--${item.status}${item.status === "target" ? " is-target" : ""}`} />
                <span>
                  <b>{item.node.name}</b>
                  <em>
                    {KIND_LABEL[item.node.kind]} · {STATUS_LABEL[item.status]}
                    {item.node.kind === PLAN_KIND.TASK && item.node.progress != null ? ` · ${item.node.progress}%` : ""}
                  </em>
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
